import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BadgeCheck, BarChart3, CreditCard, FileText, Loader2, LogIn, Receipt, RefreshCcw, Shield, User, Wallet } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import { useAuth } from '../context/AuthContext';

const BRAND_RED = '#973031';
const REV360_LOGO_SRC = '/Rev360_Light.png';
const NRS_LOGO_SRC = '/logo.png';

const SplitRoot = styled.div<{ theme: any }>`
  min-height: 100vh;
  width: 100%;
  display: flex;
  overflow: hidden;
  border-radius: 0;
`;

const PanelBase = styled.section<{ $bgVariant: 'left' | 'right' }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem;
  overflow: hidden;

  background-color: ${({ $bgVariant }) => ($bgVariant === 'left' ? '#f8fafc' : '#ffffff')};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 500px at 45% 20%, rgba(255,255,255,0.55), transparent 60%),
      radial-gradient(900px 500px at 55% 80%, rgba(255,255,255,0.45), transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    padding: 2.25rem 1.5rem;
  }
`;

const LeftPanel = styled(PanelBase)`
  align-items: flex-start;
  justify-content: space-between;
  flex: 0 0 55%;
  background-image: url('/bg-3.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (min-width: 1280px) {
    flex-basis: 60%;
  }

  /* Match html/index.html: hidden on mobile */
  @media (max-width: 1023px) {
    display: none;
  }
`;

const RightPanel = styled(PanelBase)`
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  background-image: url('/white-bg.jpg');
  background-repeat: no-repeat;
  background-size: cover;
  background-attachment: fixed;
  box-shadow: -20px 0 40px -10px rgba(0, 0, 0, 0.05);
`;

const LeftInner = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 720px;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 2.25rem;
`;

const BrandLogos = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
`;

const BrandLogo = styled.img<{ $variant: 'rev' | 'nrs' }>`
  width: auto;
  height: ${({ $variant }) => ($variant === 'rev' ? '80px' : '32px')};
  filter: none;

  @media (max-width: 1280px) {
    height: ${({ $variant }) => ($variant === 'rev' ? '80px' : '30px')};
  }
`;

const BrandPipe = styled.span`
  width: 1px;
  height: 34px;
  background: rgba(107, 114, 128, 0.55);
  display: inline-block;
`;

const MobileBrand = styled.div`
  display: none;
  width: 100%;
  justify-content: center;
  margin-bottom: 1.25rem;

  @media (max-width: 1023px) {
    display: flex;
  }
`;

const MobileHeadline = styled.h1`
  display: none;
  margin: 0.25rem 0 1.5rem;
  text-align: center;
  line-height: 1.04;
  font-size: 2.2rem;
  letter-spacing: -0.03em;
  color: rgba(43, 47, 54, 0.75);

  @media (max-width: 1023px) {
    display: block;
  }
`;

const SmallKicker = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.95rem;
  color: rgba(43, 47, 54, 0.75);
`;

const Headline = styled.h1`
  margin: 0;
  line-height: 1.04;
  font-size: 3.4rem;
  letter-spacing: -0.03em;
  color: #2b2f36;

  @media (max-width: 900px) {
    font-size: 2.6rem;
  }

  @media (max-width: 420px) {
    font-size: 2.25rem;
  }
`;

const HeadlineAccent = styled.span`
  color: ${BRAND_RED};
`;

const Description = styled.p`
  margin: 1.25rem 0 2rem;
  max-width: 640px;
  font-size: 1.05rem;
  line-height: 1.65;
  color: rgba(43, 47, 54, 0.72);
`;

const FeatureCard = styled.div`
  width: 100%;
  max-width: 560px;
  padding: 1.1rem 1.1rem 1.2rem;
  border-radius: 14px;
  background: rgba(243, 244, 246, 0.5);
  border: 1px solid rgba(229, 231, 235, 0.6);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(8px);
`;

const FeatureCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: ${BRAND_RED};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const TileIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 250, 251, 1);
  color: rgba(43, 47, 54, 0.6);
  border: 1px solid rgba(243, 244, 246, 1);
  flex: 0 0 auto;
`;

const TileText = styled.div`
  min-width: 0;
`;

const TileTitle = styled.div`
  font-weight: 800;
  font-size: 0.88rem;
  color: rgba(31, 41, 55, 0.9);
  line-height: 1.1;
`;

const TileSubtitle = styled.div`
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: rgba(107, 114, 128, 1);
  line-height: 1.2;
`;

const FeatureTile = styled.div`
  display: flex;
  gap: 0.65rem;
  align-items: center;
  padding: 0.75rem 0.8rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(243, 244, 246, 1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
    border-color: rgba(151, 48, 49, 0.2);
  }

  &:hover ${TileIcon} {
    background: rgba(151, 48, 49, 0.05);
  }

  &:hover ${TileTitle} {
    color: ${BRAND_RED};
  }
`;

const AndMore = styled.div`
  margin-top: 0.7rem;
  font-size: 0.85rem;
  color: ${BRAND_RED};
  font-weight: 700;
`;

const LeftFooter = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 2.2rem;
  display: flex;
  gap: 1.5rem;
  font-size: 0.75rem;
  color: rgba(107, 114, 128, 1);
  font-weight: 600;
`;

const RightInner = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.1rem;
`;

const AuthCard = styled.div`
  width: 100%;
  border-radius: 0;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
  text-align: center;
`;

const AuthTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #2b2f36;
`;

const AuthSubtitle = styled.p`
  margin: 0.5rem 0 1.4rem;
  font-size: 0.9rem;
  color: rgba(43, 47, 54, 0.65);
`;

const PrimaryButton = styled.button<{ theme: any }>`
  width: 100%;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  padding: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  background: linear-gradient(135deg, #a03d3d, #7e1515);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 3px solid rgba(151, 48, 49, 0.28);
    outline-offset: 2px;
  }
`;

const RedirectNote = styled.p`
  margin: 0.8rem 0 1.2rem;
  font-size: 0.875rem;
  color: rgba(107, 114, 128, 1);
`;

const DividerBlock = styled.div`
  margin-top: 2rem;
`;

const SmallCaps = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  font-weight: 900;
  color: rgba(43, 47, 54, 0.55);
  margin-bottom: 0.65rem;
`;

const SecondaryButton = styled.button`
  width: 100%;
  height: 40px;
  border: 1px solid rgba(151, 48, 49, 0.2);
  cursor: pointer;
  border-radius: 6px;
  padding: 0 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 1);
  color: ${BRAND_RED};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;

  &:hover {
    background: ${BRAND_RED};
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  &:focus-visible {
    outline: 3px solid rgba(151, 48, 49, 0.18);
    outline-offset: 2px;
  }
`;

const TrackRegistrationLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${BRAND_RED};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 3px solid rgba(151, 48, 49, 0.18);
    outline-offset: 3px;
    border-radius: 6px;
  }
`;

const RightFooter = styled.div`
  margin-top: 0.25rem;
  font-size: 0.78rem;
  color: rgba(43, 47, 54, 0.55);
  text-align: center;
`;

const InlineSpinner = styled(Loader2)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const GateMessage = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.08);
  text-align: left;
`;

const GateTitle = styled.div`
  font-weight: 900;
  color: #2b2f36;
  margin-bottom: 0.35rem;
`;

const GateText = styled.div`
  color: rgba(43, 47, 54, 0.7);
  line-height: 1.5;
  font-size: 0.92rem;
`;

const GateActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: 0.9rem;
`;


const LandingPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { login, logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Home');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const readGate = () => {
    const reason = sessionStorage.getItem('loginGateReason');
    if (!reason) return null;
    return {
      reason,
      status: sessionStorage.getItem('loginGateStatus'),
      taxId: sessionStorage.getItem('loginGateTaxId'),
      message: sessionStorage.getItem('loginGateMessage'),
    };
  };

  const [gate, setGate] = useState<ReturnType<typeof readGate>>(() => readGate());

  const clearGate = () => {
    sessionStorage.removeItem('loginGateReason');
    sessionStorage.removeItem('loginGateStatus');
    sessionStorage.removeItem('loginGateTaxId');
    sessionStorage.removeItem('loginGateMessage');
    setGate(null);
  };

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      if (isAuthenticated) {
        // User is already logged in, redirect to dashboard
        navigate('/dashboard');
        return;
      }

      // Store intended path for after login
      sessionStorage.setItem('intendedPath', '/dashboard');
      
      // Initiate OIDC login
      await login();
    } catch (error) {
      console.error('Login error:', error);
      showToast(toast.error(
        'Login Failed',
        'Unable to initiate login. Please try again.'
      ));
      setIsLoggingIn(false);
    }
  };

  const handleRegister = () => {
    clearGate();
    navigate('/register');
  };

  return (
    <SplitRoot theme={theme}>
      <LeftPanel $bgVariant="left">
        <LeftInner>
          <BrandRow>
            <BrandLogos>
              <BrandLogo $variant="rev" src={REV360_LOGO_SRC} alt="Rev360" />
              <BrandPipe aria-hidden="true" />
              <BrandLogo $variant="nrs" src={NRS_LOGO_SRC} alt="Nigeria Revenue Service" />
            </BrandLogos>
          </BrandRow>

          <SmallKicker>Welcome to the</SmallKicker>
          <Headline>
            Tax Consultant <br />
            <HeadlineAccent>Portal</HeadlineAccent> 
          </Headline>

          <Description>
          A digital platform for the Nigeria Revenue Service enabling tax consultants to coordinate teams, manage client filings, and oversee tax obligations with improved efficiency, transparency, and ease. 
          To get your TaxID, register on <a href="https://selfservice.nrs.gov.ng/">rev360</a> or get migrated from taxpro, then register on consultant portal.
          </Description>

          <FeatureCard>
            <FeatureCardTitle>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: BRAND_RED, display: 'inline-block' }} />
              MANAGE YOUR TEAMS AND CLIENTS
            </FeatureCardTitle>

            <FeatureGrid>
              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <User size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>My Profile</TileTitle>
                  <TileSubtitle>Manage personal details</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <Receipt size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>e-Invoicing</TileTitle>
                  <TileSubtitle>Manage e-invoicing</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <FileText size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Client Tax Filing</TileTitle>
                  <TileSubtitle>File taxes for client</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <CreditCard size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Payments</TileTitle>
                  <TileSubtitle>Make secure payments</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <BadgeCheck size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Tax Clearance</TileTitle>
                  <TileSubtitle>Download TCCs</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <Wallet size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Tax Wallet</TileTitle>
                  <TileSubtitle>View balances</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <RefreshCcw size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Refunds</TileTitle>
                  <TileSubtitle>Track requests</TileSubtitle>
                </TileText>
              </FeatureTile>

              <FeatureTile>
                <TileIcon aria-hidden="true">
                  <BarChart3 size={18} />
                </TileIcon>
                <TileText>
                  <TileTitle>Assessments</TileTitle>
                  <TileSubtitle>View compliance</TileSubtitle>
                </TileText>
              </FeatureTile>
            </FeatureGrid>

            <AndMore>and more...</AndMore>
          </FeatureCard>
        </LeftInner>

        <LeftFooter>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Shield size={12} color={BRAND_RED} /> Secured by NRS
          </span>
        </LeftFooter>
      </LeftPanel>

      <RightPanel $bgVariant="right">
        <RightInner>
          <MobileBrand>
            <img src={NRS_LOGO_SRC} alt="Nigeria Revenue Service" style={{ height: 48, width: 'auto' }} />
          </MobileBrand>
          <MobileHeadline>
            Tax Consultant <br />
            <HeadlineAccent>Portal</HeadlineAccent>
          </MobileHeadline>

          <AuthCard>
            <AuthTitle>Login to your account</AuthTitle>
            <AuthSubtitle>Sign in with NRS SSO below to continue</AuthSubtitle>

            {!gate && (
              <PrimaryButton theme={theme} onClick={handleLogin} disabled={isLoggingIn}>
                {isLoggingIn ? <InlineSpinner size={18} /> : <LogIn size={18} />}
                {isLoggingIn
                  ? 'Processing...'
                  : (isAuthenticated ? `Continue as ${user?.profile?.name || 'User'}` : 'Continue with NRS SSO')}
              </PrimaryButton>
            )}

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <RedirectNote>You will be redirected to the NRS Single Sign-On portal</RedirectNote>
            </div>

            {gate && (
              <GateMessage>
                <GateTitle>
                  {gate.reason === 'pending'
                    ? 'Registration Pending'
                    : gate.reason === 'profile_error'
                      ? 'Profile Error'
                      : 'Not a Tax Consultant'}
                </GateTitle>
                <GateText>
                  {gate.reason === 'pending'
                    ? `You cannot login yet because your registration is still pending${gate.status ? ` (${gate.status})` : ''}.`
                    : (gate.message || (
                      gate.reason === 'profile_error'
                        ? 'We could not load your consultant profile. Please try again.'
                        : 'This account is not registered as a Tax Consultant.'
                    ))}
                  {gate.taxId ? ` Tax ID: ${gate.taxId}.` : ''}
                </GateText>
                <GateActions>
                  <SecondaryButton onClick={() => {
                    clearGate();
                    navigate(gate.taxId ? `/track?taxId=${encodeURIComponent(gate.taxId)}` : '/track');
                  }}>
                    Track Registration <ArrowRight size={18} />
                  </SecondaryButton>
                  <SecondaryButton onClick={() => {
                    clearGate();
                    setIsLoggingIn(false);
                    setIsSigningOut(true);
                    // Ensure IdP session is cleared so user can pick a different account
                    logout().catch((e) => {
                      console.error('Logout error:', e);
                      showToast(toast.error('Logout Failed', 'Unable to sign out. Please try again.'));
                      setIsSigningOut(false);
                    });
                  }}>
                    {isSigningOut ? 'Signing out...' : 'Try a different account'}
                  </SecondaryButton>
                </GateActions>
              </GateMessage>
            )}

            <DividerBlock>
              <div style={{ background: 'rgba(249, 250, 251, 1)', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
                <SmallCaps>NEW TAXPAYER?</SmallCaps>
                <SecondaryButton onClick={handleRegister}>
                  Create New Account <ArrowRight size={18} />
                </SecondaryButton>
                <TrackRegistrationLink to="/track">Track Registration</TrackRegistrationLink>
              </div>
            </DividerBlock>
          </AuthCard>

          <RightFooter>© {new Date().getFullYear()} Nigeria Revenue Service. All rights reserved.</RightFooter>
        </RightInner>
      </RightPanel>
    </SplitRoot>
  );
};

export default LandingPage;
